/* ------------------------------------------------------------------ *
 *  Minimal keccak-256 + EIP-55 checksum — zero external dependencies  *
 * ------------------------------------------------------------------ */

const MASK64 = BigInt("0xffffffffffffffff");
const RATE = 136; // bytes (keccak-256 rate = 1600 − 2×256 = 1088 bits)

// Round constants for keccak-f[1600]
const RC: bigint[] = [
  BigInt("0x0000000000000001"),
  BigInt("0x0000000000008082"),
  BigInt("0x800000000000808a"),
  BigInt("0x8000000080008000"),
  BigInt("0x000000000000808b"),
  BigInt("0x0000000080000001"),
  BigInt("0x8000000080008081"),
  BigInt("0x8000000000008009"),
  BigInt("0x000000000000008a"),
  BigInt("0x0000000000000088"),
  BigInt("0x0000000080008009"),
  BigInt("0x000000008000000a"),
  BigInt("0x000000008000808b"),
  BigInt("0x800000000000008b"),
  BigInt("0x8000000000008089"),
  BigInt("0x8000000000008003"),
  BigInt("0x8000000000008002"),
  BigInt("0x8000000000000080"),
  BigInt("0x000000000000800a"),
  BigInt("0x800000008000000a"),
  BigInt("0x8000000080008081"),
  BigInt("0x8000000000008080"),
  BigInt("0x0000000080000001"),
  BigInt("0x8000000080008008"),
];

// ρ rotation offsets indexed by [x + 5y]
const ROT = [
  0, 1, 62, 28, 27, 36, 44, 6, 55, 20, 3, 10, 43, 25, 39, 41, 45, 15, 21, 8, 18, 2, 61, 56, 14,
];

function rot64(x: bigint, n: number): bigint {
  return ((x << BigInt(n)) | (x >> BigInt(64 - n))) & MASK64;
}

function keccakF(s: bigint[]): void {
  for (let r = 0; r < 24; r++) {
    // θ — column parity
    const c0 = s[0]! ^ s[5]! ^ s[10]! ^ s[15]! ^ s[20]!;
    const c1 = s[1]! ^ s[6]! ^ s[11]! ^ s[16]! ^ s[21]!;
    const c2 = s[2]! ^ s[7]! ^ s[12]! ^ s[17]! ^ s[22]!;
    const c3 = s[3]! ^ s[8]! ^ s[13]! ^ s[18]! ^ s[23]!;
    const c4 = s[4]! ^ s[9]! ^ s[14]! ^ s[19]! ^ s[24]!;
    const d0 = c4 ^ rot64(c1, 1);
    const d1 = c0 ^ rot64(c2, 1);
    const d2 = c1 ^ rot64(c3, 1);
    const d3 = c2 ^ rot64(c4, 1);
    const d4 = c3 ^ rot64(c0, 1);
    for (let y = 0; y < 25; y += 5) {
      s[y]! ^= d0;
      s[y + 1]! ^= d1;
      s[y + 2]! ^= d2;
      s[y + 3]! ^= d3;
      s[y + 4]! ^= d4;
    }

    // ρ + π — rotate lanes and move to new positions
    const t = new Array<bigint>(25);
    for (let x = 0; x < 5; x++) {
      for (let y = 0; y < 5; y++) {
        const i = x + 5 * y;
        t[y + 5 * ((2 * x + 3 * y) % 5)] = rot64(s[i]!, ROT[i]!);
      }
    }

    // χ — non-linear step (~t needs MASK64 because BigInt NOT has infinite precision)
    for (let y = 0; y < 25; y += 5) {
      const t0 = t[y]!,
        t1 = t[y + 1]!,
        t2 = t[y + 2]!,
        t3 = t[y + 3]!,
        t4 = t[y + 4]!;
      s[y] = t0 ^ (~t1 & MASK64 & t2);
      s[y + 1] = t1 ^ (~t2 & MASK64 & t3);
      s[y + 2] = t2 ^ (~t3 & MASK64 & t4);
      s[y + 3] = t3 ^ (~t4 & MASK64 & t0);
      s[y + 4] = t4 ^ (~t0 & MASK64 & t1);
    }

    // ι — round constant
    s[0]! ^= RC[r]!;
  }
}

/** Keccak-256 hash → hex string (no 0x prefix). */
function keccak256Hex(data: Uint8Array): string {
  // Pad: data ‖ 0x01 ‖ 0x00…0x00 ‖ 0x80  (keccak padding, NOT SHA-3 0x06)
  const blocks = Math.ceil((data.length + 1) / RATE);
  const padded = new Uint8Array(blocks * RATE);
  padded.set(data);
  padded[data.length] = 0x01;
  padded[padded.length - 1]! |= 0x80;

  // Absorb
  const s = new Array<bigint>(25).fill(BigInt(0));
  const dv = new DataView(padded.buffer);
  for (let off = 0; off < padded.length; off += RATE) {
    for (let i = 0; i < 17; i++) s[i]! ^= dv.getBigUint64(off + i * 8, true);
    keccakF(s);
  }

  // Squeeze 32 bytes → hex
  const out = new Uint8Array(32);
  const ov = new DataView(out.buffer);
  for (let i = 0; i < 4; i++) ov.setBigUint64(i * 8, s[i]!, true);
  let hex = "";
  for (const b of out) hex += b.toString(16).padStart(2, "0");
  return hex;
}

/* ------------------------------------------------------------------ *
 *  EIP-55 checksum                                                    *
 * ------------------------------------------------------------------ */

const ETH_ADDRESS_RE = /^0x[0-9a-fA-F]{40}$/;
const encoder = new TextEncoder();

/**
 * EIP-55 mixed-case checksum encoding for Ethereum addresses.
 * Equivalent to `getAddress` from ethers — zero dependencies.
 *
 * @see https://eips.ethereum.org/EIPS/eip-55
 */
export function toChecksumAddress(address: string): string {
  if (!ETH_ADDRESS_RE.test(address)) {
    throw new Error(`Invalid Ethereum address: ${address}`);
  }

  const lower = address.slice(2).toLowerCase();
  const hash = keccak256Hex(encoder.encode(lower));

  let out = "0x";
  for (let i = 0; i < 40; i++) {
    // Hex digits a-f are uppercased when the corresponding hash nibble >= 8
    out += Number.parseInt(hash[i]!, 16) >= 8 ? lower[i]!.toUpperCase() : lower[i]!;
  }
  return out;
}
